import React, { useState, useRef, useCallback, useEffect, useImperativeHandle, forwardRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { PlaceObject } from './ObjectForm';
import { getAllModules } from '../../utils/memoryPalaceUtils';
import ImageGeneratorModal from '../shared/ImageGeneratorModal';
import { generateImageForObject, uploadObjectImage } from '../../services/imageService';
import './MemoryPalace.css';

interface ObjectsCanvasProps {
  objects: PlaceObject[];
  onObjectMove: (id: string, x: number, y: number) => void;
  onObjectClick: (object: PlaceObject) => void;
  onObjectDelete: (id: string) => void;
  onObjectDoubleClick: (object: PlaceObject) => void;
  onNavigateToPlace?: (placeId: number) => void;
  onObjectUpdate?: (object: PlaceObject) => void;
  placeId?: number;
}

export interface ObjectsCanvasRef {
  getVisibleCenter: () => { x: number; y: number } | null;
}

const ObjectsCanvas = forwardRef<ObjectsCanvasRef, ObjectsCanvasProps>(({
  objects,
  onObjectMove,
  onObjectClick,
  onObjectDelete,
  onObjectDoubleClick,
  onNavigateToPlace,
  onObjectUpdate,
  placeId
}, ref) => {
  const navigate = useNavigate();
  const modules = getAllModules();
  const canvasRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const selectionPopoverRef = useRef<HTMLDivElement>(null);
  const [selectedObjectId, setSelectedObjectId] = useState<string | null>(null);
  const [draggingObjectId, setDraggingObjectId] = useState<string | null>(null);
  const [dragStart, setDragStart] = useState<{ x: number; y: number } | null>(null);
  const [objectStartPos, setObjectStartPos] = useState<{ x: number; y: number } | null>(null);
  const [selectionPopover, setSelectionPopover] = useState<{ object: PlaceObject; x: number; y: number } | null>(null);
  const [clickStartPos, setClickStartPos] = useState<{ x: number; y: number } | null>(null);
  const [clickedObjectId, setClickedObjectId] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState<{ x: number; y: number } | null>(null);
  const [isSpacePressed, setIsSpacePressed] = useState(false);
  const [showImageGeneratorModal, setShowImageGeneratorModal] = useState(false);
  const [selectedObjectForImage, setSelectedObjectForImage] = useState<PlaceObject | null>(null);
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);
  
  // Expose method to get visible center position
  useImperativeHandle(ref, () => ({
    getVisibleCenter: (): { x: number; y: number } | null => {
      if (!canvasRef.current || !gridRef.current) {
        return null;
      }
      
      const rect = canvasRef.current.getBoundingClientRect();
      const scrollX = canvasRef.current.scrollLeft;
      const scrollY = canvasRef.current.scrollTop;
      
      // Calculate center of visible viewport
      const centerX = rect.width / 2;
      const centerY = rect.height / 2;
      
      // Convert screen center to grid coordinates
      const gridX = (centerX + scrollX - pan.x) / zoom;
      const gridY = (centerY + scrollY - pan.y) / zoom;
      
      return { x: Math.round(gridX), y: Math.round(gridY) };
    }
  }), [zoom, pan]);
  
  // Helper to check if object has any link (place or module)
  const hasLink = (object: PlaceObject): boolean => {
    return !!(object.connectedPlaceId || (object.linkedModulePath && object.linkedItemId));
  };
  
  // Get module label for display
  const getModuleLabel = (modulePath: string): string => {
    const module = modules.find(m => m.path === modulePath);
    return module ? module.label : modulePath;
  };

  const handleMouseDown = useCallback((e: React.MouseEvent, object: PlaceObject) => {
    e.stopPropagation();
    
    // Middle mouse button for panning (but not on objects - let it drag)
    if (e.button === 1) {
      e.preventDefault();
      setIsPanning(true);
      setPanStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
      return;
    }
    
    // Right-click: prevent default context menu
    if (e.button === 2) {
      e.preventDefault();
      return;
    }
    
    // Left-click: allow dragging for all objects
    // Note: Panning with spacebar/shift is disabled when clicking on objects
    if (e.button === 0) {
      setSelectedObjectId(object.id);
      setDraggingObjectId(object.id);
      setClickedObjectId(object.id);
      setClickStartPos({ x: e.clientX, y: e.clientY });
      
      const rect = canvasRef.current?.getBoundingClientRect();
      if (rect && gridRef.current && canvasRef.current) {
        // Account for zoom, pan, and scroll when calculating drag start
        const scrollX = canvasRef.current.scrollLeft;
        const scrollY = canvasRef.current.scrollTop;
        const relativeX = (e.clientX - rect.left + scrollX - pan.x) / zoom;
        const relativeY = (e.clientY - rect.top + scrollY - pan.y) / zoom;
        
        setDragStart({
          x: relativeX,
          y: relativeY
        });
        setObjectStartPos({
          x: object.x,
          y: object.y
        });
      }
      
      onObjectClick(object);
    }
  }, [onObjectClick, pan, zoom]);

  const handleTouchStart = useCallback((e: React.TouchEvent, object: PlaceObject) => {
    e.stopPropagation();
    e.preventDefault(); // Prevent scrolling when starting a drag on an object
    const touch = e.touches[0];
    if (!touch) return;

    // Touch on object always initiates object dragging, not panning
    setSelectedObjectId(object.id);
    setDraggingObjectId(object.id);
    setClickedObjectId(object.id);
    setClickStartPos({ x: touch.clientX, y: touch.clientY });
    
    const rect = canvasRef.current?.getBoundingClientRect();
    if (rect && canvasRef.current) {
      // Account for zoom, pan, and scroll when calculating drag start
      const scrollX = canvasRef.current.scrollLeft;
      const scrollY = canvasRef.current.scrollTop;
      const relativeX = (touch.clientX - rect.left + scrollX - pan.x) / zoom;
      const relativeY = (touch.clientY - rect.top + scrollY - pan.y) / zoom;
      
      setDragStart({
        x: relativeX,
        y: relativeY
      });
      setObjectStartPos({
        x: object.x,
        y: object.y
      });
    }
    
    onObjectClick(object);
  }, [onObjectClick, pan, zoom]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    // Handle panning (only if not dragging an object)
    if (isPanning && panStart && !draggingObjectId && canvasRef.current) {
      const scrollX = canvasRef.current.scrollLeft;
      const scrollY = canvasRef.current.scrollTop;
      setPan({
        x: e.clientX + scrollX - panStart.x,
        y: e.clientY + scrollY - panStart.y
      });
      return;
    }

    // Handle object dragging (takes priority over panning)
    if (draggingObjectId && dragStart && objectStartPos && canvasRef.current && gridRef.current) {
      // If mouse moved significantly, clear clickedObjectId to prevent popup menu
      if (clickStartPos) {
        const moveDistance = Math.sqrt(
          Math.pow(e.clientX - clickStartPos.x, 2) + 
          Math.pow(e.clientY - clickStartPos.y, 2)
        );
        if (moveDistance > 5) {
          setClickedObjectId(null);
        }
      }

      const rect = canvasRef.current.getBoundingClientRect();
      const scrollX = canvasRef.current.scrollLeft;
      const scrollY = canvasRef.current.scrollTop;
      
      // Convert mouse position to grid coordinates (accounting for zoom and pan)
      const relativeX = (e.clientX - rect.left + scrollX - pan.x) / zoom;
      const relativeY = (e.clientY - rect.top + scrollY - pan.y) / zoom;

      // Calculate delta in grid coordinates
      const deltaX = relativeX - dragStart.x;
      const deltaY = relativeY - dragStart.y;

      const newX = Math.max(0, objectStartPos.x + deltaX);
      const newY = Math.max(0, objectStartPos.y + deltaY);

      onObjectMove(draggingObjectId, newX, newY);
    }
  }, [draggingObjectId, dragStart, objectStartPos, onObjectMove, clickStartPos, isPanning, panStart, pan, zoom]);

  const handleTouchMove = useCallback((e: TouchEvent) => {
    const touch = e.touches[0];
    if (!touch) return;

    // Handle object dragging (takes priority)
    if (draggingObjectId && dragStart && objectStartPos && canvasRef.current) {
      e.preventDefault(); // Prevent scrolling while dragging

      // If touch moved significantly, clear clickedObjectId to prevent popup menu
      if (clickStartPos) {
        const moveDistance = Math.sqrt(
          Math.pow(touch.clientX - clickStartPos.x, 2) + 
          Math.pow(touch.clientY - clickStartPos.y, 2)
        );
        if (moveDistance > 5) {
          setClickedObjectId(null);
        }
      }

      const rect = canvasRef.current.getBoundingClientRect();
      const scrollX = canvasRef.current.scrollLeft;
      const scrollY = canvasRef.current.scrollTop;
      
      // Convert touch position to grid coordinates (accounting for zoom and pan)
      const relativeX = (touch.clientX - rect.left + scrollX - pan.x) / zoom;
      const relativeY = (touch.clientY - rect.top + scrollY - pan.y) / zoom;

      // Calculate delta in grid coordinates
      const deltaX = relativeX - dragStart.x;
      const deltaY = relativeY - dragStart.y;

      const newX = Math.max(0, objectStartPos.x + deltaX);
      const newY = Math.max(0, objectStartPos.y + deltaY);

      console.log('--- Dragging ---');
      console.log(`touch: (${touch.clientX}, ${touch.clientY})`);
      console.log(`relative: (${relativeX}, ${relativeY})`);
      console.log(`delta: (${deltaX}, ${deltaY})`);
      console.log(`newPos: (${newX}, ${newY})`);
      console.log(`zoom: ${zoom}, pan: (${pan.x}, ${pan.y})`);

      onObjectMove(draggingObjectId, newX, newY);
      return;
    }

    // Handle panning (only if not dragging an object)
    if (isPanning && panStart && !draggingObjectId && canvasRef.current) {
      const scrollX = canvasRef.current.scrollLeft;
      const scrollY = canvasRef.current.scrollTop;
      const newPanX = touch.clientX + scrollX - panStart.x;
      const newPanY = touch.clientY + scrollY - panStart.y;
      
      console.log('--- Panning ---');
      console.log(`touch: (${touch.clientX}, ${touch.clientY})`);
      console.log(`newPan: (${newPanX}, ${newPanY})`);

      setPan({
        x: newPanX,
        y: newPanY
      });
    }
  }, [draggingObjectId, dragStart, objectStartPos, onObjectMove, clickStartPos, isPanning, panStart, pan, zoom]);

  const handleMouseUp = useCallback((e?: MouseEvent) => {
    // On mouse up, if it was a click (not a drag), show selection popover
    // clickedObjectId is cleared if we dragged more than 5px, so if it's still set, it was a click
    if (e && clickedObjectId && !isPanning && canvasRef.current && gridRef.current) {
      const clickedObject = objects.find(obj => obj.id === clickedObjectId);
      if (clickedObject) {
        // Calculate position relative to the object on the canvas
        const canvasRect = canvasRef.current.getBoundingClientRect();
        const scrollX = canvasRef.current.scrollLeft;
        const scrollY = canvasRef.current.scrollTop;
        
        // Convert object position to screen coordinates
        // The grid is transformed by: translate(pan.x, pan.y) scale(zoom)
        // Object's transformed position in the grid: (object.x * zoom + pan.x, object.y * zoom + pan.y)
        // But this position is relative to the canvas content area, and canvas is scrollable
        // Screen position = canvasRect position + transformed position - scroll offset
        const transformedX = clickedObject.x * zoom + pan.x;
        const transformedY = clickedObject.y * zoom + pan.y;
        
        const objectScreenX = canvasRect.left + transformedX - scrollX;
        const objectScreenY = canvasRect.top + transformedY - scrollY;
        
        // Position popover below the object with a small gap
        const popoverX = objectScreenX;
        const popoverY = objectScreenY + (clickedObject.height * zoom) + 5;
        
        setSelectionPopover({
          object: clickedObject,
          x: popoverX,
          y: popoverY
        });
      }
    }
    
    // Clean up states
    setIsPanning(false);
    setPanStart(null);
    setDraggingObjectId(null);
    setDragStart(null);
    setObjectStartPos(null);
    setClickStartPos(null);
    setClickedObjectId(null);
  }, [clickedObjectId, objects, isPanning, zoom, pan]);

  const handleTouchEnd = useCallback((e?: TouchEvent) => {
    // On touch end, if it was a tap (not a drag), show selection popover
    if (e && clickedObjectId && !isPanning && canvasRef.current && gridRef.current) {
      const clickedObject = objects.find(obj => obj.id === clickedObjectId);
      if (clickedObject) {
        // Calculate position relative to the object on the canvas
        const canvasRect = canvasRef.current.getBoundingClientRect();
        const scrollX = canvasRef.current.scrollLeft;
        const scrollY = canvasRef.current.scrollTop;
        
        // Convert object position to screen coordinates
        // The grid is transformed by: translate(pan.x, pan.y) scale(zoom)
        // Object's transformed position in the grid: (object.x * zoom + pan.x, object.y * zoom + pan.y)
        // But this position is relative to the canvas content area, and canvas is scrollable
        // Screen position = canvasRect position + transformed position - scroll offset
        const transformedX = clickedObject.x * zoom + pan.x;
        const transformedY = clickedObject.y * zoom + pan.y;
        
        const objectScreenX = canvasRect.left + transformedX - scrollX;
        const objectScreenY = canvasRect.top + transformedY - scrollY;
        
        // Position popover below the object with a small gap
        const popoverX = objectScreenX;
        const popoverY = objectScreenY + (clickedObject.height * zoom) + 5;
        
        setSelectionPopover({
          object: clickedObject,
          x: popoverX,
          y: popoverY
        });
      }
    }
    
    // Clean up states (similar to mouse up)
    setIsPanning(false);
    setPanStart(null);
    setDraggingObjectId(null);
    setDragStart(null);
    setObjectStartPos(null);
    setClickStartPos(null);
    setClickedObjectId(null);
  }, [clickedObjectId, objects, isPanning, zoom, pan]);

  useEffect(() => {
    if (draggingObjectId || clickedObjectId || isPanning) {
      const mouseUpHandler = (e: MouseEvent) => handleMouseUp(e);
      const touchEndHandler = (e: TouchEvent) => handleTouchEnd(e);
      
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', mouseUpHandler);
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', touchEndHandler);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', mouseUpHandler);
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', touchEndHandler);
      };
    }
  }, [draggingObjectId, clickedObjectId, isPanning, handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]);

  // Handle canvas panning (middle mouse or spacebar + drag)
  const handleCanvasMouseDown = useCallback((e: React.MouseEvent) => {
    // Middle mouse button or spacebar/shift + left click for panning
    if (e.button === 1 || (e.button === 0 && (isSpacePressed || e.shiftKey))) {
      e.preventDefault();
      if (canvasRef.current) {
        const scrollX = canvasRef.current.scrollLeft;
        const scrollY = canvasRef.current.scrollTop;
        setIsPanning(true);
        setPanStart({ 
          x: e.clientX + scrollX - pan.x, 
          y: e.clientY + scrollY - pan.y 
        });
      }
    }
  }, [pan, isSpacePressed]);

  // Track spacebar for panning (only when not typing in inputs)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const isInput = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;
      
      if (e.code === 'Space' && !isInput) {
        setIsSpacePressed(true);
      }
    };
    
    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.code === 'Space') {
        setIsSpacePressed(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keyup', handleKeyUp);
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  // Handle wheel zoom
  const handleWheel = useCallback((e: React.WheelEvent) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      const delta = e.deltaY > 0 ? 0.9 : 1.1;
      const newZoom = Math.max(0.25, Math.min(3, zoom * delta));
      
      if (canvasRef.current && gridRef.current) {
        const rect = canvasRef.current.getBoundingClientRect();
        
        // Get mouse position relative to canvas viewport
        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;
        
        // Get current scroll position
        const scrollX = canvasRef.current.scrollLeft;
        const scrollY = canvasRef.current.scrollTop;
        
        // Calculate the point in the grid's coordinate system before zoom
        const gridX = (mouseX + scrollX - pan.x) / zoom;
        const gridY = (mouseY + scrollY - pan.y) / zoom;
        
        // Calculate new pan to keep the same grid point under the mouse
        const newPanX = mouseX + scrollX - gridX * newZoom;
        const newPanY = mouseY + scrollY - gridY * newZoom;
        
        setZoom(newZoom);
        setPan({ x: newPanX, y: newPanY });
      }
    }
  }, [zoom, pan]);

  // Zoom controls
  const handleZoomIn = useCallback(() => {
    if (!canvasRef.current) return;
    const newZoom = Math.min(3, zoom * 1.2);
    const rect = canvasRef.current.getBoundingClientRect();
    const scrollX = canvasRef.current.scrollLeft;
    const scrollY = canvasRef.current.scrollTop;
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    
    const gridX = (centerX + scrollX - pan.x) / zoom;
    const gridY = (centerY + scrollY - pan.y) / zoom;
    
    const newPanX = centerX + scrollX - gridX * newZoom;
    const newPanY = centerY + scrollY - gridY * newZoom;
    
    setZoom(newZoom);
    setPan({ x: newPanX, y: newPanY });
  }, [zoom, pan]);

  const handleZoomOut = useCallback(() => {
    if (!canvasRef.current) return;
    const newZoom = Math.max(0.25, zoom / 1.2);
    const rect = canvasRef.current.getBoundingClientRect();
    const scrollX = canvasRef.current.scrollLeft;
    const scrollY = canvasRef.current.scrollTop;
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    
    const gridX = (centerX + scrollX - pan.x) / zoom;
    const gridY = (centerY + scrollY - pan.y) / zoom;
    
    const newPanX = centerX + scrollX - gridX * newZoom;
    const newPanY = centerY + scrollY - gridY * newZoom;
    
    setZoom(newZoom);
    setPan({ x: newPanX, y: newPanY });
  }, [zoom, pan]);

  const handleResetZoom = useCallback(() => {
    if (!canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const scrollX = canvasRef.current.scrollLeft;
    const scrollY = canvasRef.current.scrollTop;
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    
    // Calculate what grid point is at the center
    const gridX = (centerX + scrollX - pan.x) / zoom;
    const gridY = (centerY + scrollY - pan.y) / zoom;
    
    // Reset zoom and adjust pan to keep the same grid point at center
    const newPanX = centerX + scrollX - gridX;
    const newPanY = centerY + scrollY - gridY;
    
    setZoom(1);
    setPan({ x: newPanX, y: newPanY });
  }, [zoom, pan]);

  const handleCanvasClick = (e: React.MouseEvent) => {
    // Don't handle clicks if panning
    if (isPanning) {
      return;
    }
    
    // Check if clicking on an object
    const target = e.target as HTMLElement;
    const isClickingObject = target.closest('.memory-palace-object-rectangle') || target.closest('.memory-palace-object-wrapper');
    
    // Don't close popover if clicking on an object (the object will handle showing its own popover)
    if (isClickingObject) {
      return;
    }
    
    // Close selection popover if clicking outside
    if (selectionPopoverRef.current && !selectionPopoverRef.current.contains(e.target as Node)) {
      setSelectionPopover(null);
    }
    
    // Only deselect if clicking on the grid background (not on an object)
    if (target.classList.contains('memory-palace-objects-grid') || target === canvasRef.current) {
      setSelectedObjectId(null);
      setSelectionPopover(null);
    }
  };

  const handleCanvasTouchStart = (e: React.TouchEvent) => {
    // Check if touching an object
    const target = e.target as HTMLElement;
    const isTouchingObject = target.closest('.memory-palace-object-rectangle') || target.closest('.memory-palace-object-wrapper');
    
    // Don't close popover if touching an object (the object will handle showing its own popover)
    if (isTouchingObject) {
      return;
    }
    
    // Close selection popover if touching outside
    if (selectionPopoverRef.current && !selectionPopoverRef.current.contains(e.target as Node)) {
      setSelectionPopover(null);
    }
    
    // Only deselect if touching the grid background (not on an object)
    if (target.classList.contains('memory-palace-objects-grid') || target === canvasRef.current) {
      setSelectedObjectId(null);
      setSelectionPopover(null);
    }
  };

  const handleDeleteClick = (e: React.MouseEvent, objectId: string, object: PlaceObject) => {
    e.stopPropagation();
    // Don't allow deletion of linked objects (connected places or linked modules)
    if (hasLink(object)) {
      return;
    }
    if (window.confirm('Are you sure you want to delete this object?')) {
      onObjectDelete(objectId);
      setSelectedObjectId(null);
    }
  };

  const handleNavigateClick = (e: React.MouseEvent, placeId: number) => {
    e.stopPropagation();
    if (onNavigateToPlace) {
      onNavigateToPlace(placeId);
      setSelectionPopover(null);
      setSelectedObjectId(null);
    }
  };
  
  const handleNavigateToModule = (e: React.MouseEvent, object: PlaceObject) => {
    e.stopPropagation();
    if (object.linkedModulePath && object.linkedItemId) {
      navigate(`${object.linkedModulePath}/${object.linkedItemId}`);
      setSelectionPopover(null);
      setSelectedObjectId(null);
    } else if (object.linkedModulePath) {
      navigate(object.linkedModulePath);
      setSelectionPopover(null);
      setSelectedObjectId(null);
    }
  };
  
  const handleOpenLinkedPage = (e: React.MouseEvent, object: PlaceObject) => {
    e.stopPropagation();
    if (object.connectedPlaceId && onNavigateToPlace) {
      handleNavigateClick(e, object.connectedPlaceId);
    } else if (object.linkedModulePath) {
      handleNavigateToModule(e, object);
    }
  };

  const handleOpenImageGenerator = (e: React.MouseEvent, object: PlaceObject) => {
    e.stopPropagation();
    setSelectedObjectForImage(object);
    setShowImageGeneratorModal(true);
    setSelectionPopover(null);
  };

  const handleGenerateImage = async (prompt: string, provider?: 'gemini' | 'grok') => {
    if (!selectedObjectForImage || !placeId || !onObjectUpdate) {
      return;
    }

    try {
      setIsGeneratingImage(true);
      
      // Generate image
      const imageData = await generateImageForObject(selectedObjectForImage.id, prompt, provider);
      
      // Upload to Firebase and update object
      const imageUrl = await uploadObjectImage(
        placeId,
        selectedObjectForImage.id,
        imageData.image,
        imageData.mimeType,
        prompt
      );
      
      // Update object with image URL
      const updatedObject: PlaceObject = {
        ...selectedObjectForImage,
        image_url: imageUrl,
        image_prompt: prompt
      };
      
      onObjectUpdate(updatedObject);
      
      setShowImageGeneratorModal(false);
      setSelectedObjectForImage(null);
    } catch (error) {
      console.error('Error generating image:', error);
      alert('Failed to generate image. Please try again.');
    } finally {
      setIsGeneratingImage(false);
    }
  };

  // Close selection popover on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectionPopoverRef.current && !selectionPopoverRef.current.contains(event.target as Node)) {
        setSelectionPopover(null);
      }
    };

    if (selectionPopover) {
      // Add a small delay before attaching the click handler to prevent immediate closure
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 100);
      
      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [selectionPopover]);

  return (
    <div 
      className={`memory-palace-objects-canvas ${isPanning ? 'panning' : ''}`}
      ref={canvasRef}
      onWheel={handleWheel}
      onMouseDown={handleCanvasMouseDown}
    >
      <div 
        className="memory-palace-objects-grid"
        ref={gridRef}
        onClick={handleCanvasClick}
        onTouchStart={handleCanvasTouchStart}
        onMouseDown={(e) => {
          // Prevent middle mouse button from scrolling
          if (e.button === 1) {
            e.preventDefault();
          }
        }}
        style={{
          transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
          transformOrigin: '0 0'
        }}
      >
        {objects.map((object) => {
          const isSelected = selectedObjectId === object.id;
          const isDragging = draggingObjectId === object.id;
          const connectionClass = object.connectedPlaceId 
            ? `connected-place ${object.connectionDirection === 'outgoing' ? 'connected-place-outgoing' : object.connectionDirection === 'incoming' ? 'connected-place-incoming' : ''}`
            : '';
          
          return (
            <div
              key={object.id}
              className="memory-palace-object-wrapper"
              style={{
                left: `${object.x}px`,
                top: `${object.y}px`,
                width: `${object.width}px`
              }}
            >
              <div
                className={`memory-palace-object-rectangle ${isSelected ? 'selected' : ''} ${isDragging ? 'dragging' : ''} ${connectionClass} ${object.image_url ? 'has-image' : ''}`}
                style={{
                  width: `${object.width}px`,
                  height: `${object.height}px`,
                  backgroundImage: object.image_url ? `url(${object.image_url})` : undefined,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                  touchAction: 'none' // Prevent native browser panning/scrolling on the object
                }}
                onMouseDown={(e) => handleMouseDown(e, object)}
                onContextMenu={(e) => {
                  // Prevent default context menu
                  e.preventDefault();
                }}
                onTouchStart={(e) => handleTouchStart(e, object)}
              >
                {!object.image_url && (
                  <div className="memory-palace-object-label">
                    {object.name ? (
                      <div className="memory-palace-object-name">{object.name}</div>
                    ) : (
                      <div className="memory-palace-object-name">{object.type}</div>
                    )}
                  </div>
                )}
              </div>
              {object.image_url && object.name && (
                <div className="memory-palace-object-label-below">
                  <div className="memory-palace-object-name">{object.name}</div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      
      {(() => {
        // console.log('Render check - selectionPopover:', selectionPopover);
        return selectionPopover && createPortal(
        <div
          ref={selectionPopoverRef}
          className="memory-palace-object-popup-menu"
          style={{
            position: 'fixed',
            left: `${selectionPopover.x}px`,
            top: `${selectionPopover.y}px`,
            zIndex: 2000,
            backgroundColor: '#ffffff'
          }}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          {!selectionPopover.object.connectedPlaceId && (
            <button
              className="memory-palace-object-popup-menu-item"
              onClick={(e) => {
                e.stopPropagation();
                onObjectDoubleClick(selectionPopover.object);
                setSelectionPopover(null);
              }}
            >
              Edit
            </button>
          )}
          {!selectionPopover.object.connectedPlaceId && placeId && onObjectUpdate && (
            <button
              className="memory-palace-object-popup-menu-item"
              onClick={(e) => handleOpenImageGenerator(e, selectionPopover.object)}
            >
              🖼️ Generate Image
            </button>
          )}
          {hasLink(selectionPopover.object) && (
            <button
              className="memory-palace-object-popup-menu-item"
              onClick={(e) => handleOpenLinkedPage(e, selectionPopover.object)}
            >
              Go to Linked Page
            </button>
          )}
          {!selectionPopover.object.connectedPlaceId && (
            <button
              className="memory-palace-object-popup-menu-item"
              onClick={(e) => {
                e.stopPropagation();
                if (window.confirm('Are you sure you want to delete this object?')) {
                  onObjectDelete(selectionPopover.object.id);
                  setSelectionPopover(null);
                  setSelectedObjectId(null);
                }
              }}
            >
              Delete
            </button>
          )}
        </div>,
        document.body
      );
      })()}

      {/* Zoom Controls */}
      <div className="memory-palace-objects-zoom-controls">
        <button
          className="memory-palace-objects-zoom-btn"
          onClick={handleZoomIn}
          title="Zoom In (Ctrl/Cmd + Scroll)"
        >
          +
        </button>
        <button
          className="memory-palace-objects-zoom-btn"
          onClick={handleZoomOut}
          title="Zoom Out (Ctrl/Cmd + Scroll)"
        >
          −
        </button>
        <button
          className="memory-palace-objects-zoom-btn"
          onClick={handleResetZoom}
          title="Reset Zoom"
        >
          ⌂
        </button>
        <div className="memory-palace-objects-zoom-level">
          {Math.round(zoom * 100)}%
        </div>
      </div>

      {/* Image Generator Modal */}
      <ImageGeneratorModal
        isOpen={showImageGeneratorModal}
        onClose={() => {
          setShowImageGeneratorModal(false);
          setSelectedObjectForImage(null);
        }}
        onGenerate={handleGenerateImage}
        initialPrompt={selectedObjectForImage?.name || selectedObjectForImage?.type || ''}
        isGenerating={isGeneratingImage}
        hintText="Provide a detailed description of the image you want to generate for this object."
      />
    </div>
  );
});

ObjectsCanvas.displayName = 'ObjectsCanvas';

export default ObjectsCanvas;

